import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { BACK_TO_SCHOOL_2026, getBackToSchool2026TimeLeft } from '../../../utils/backToSchool2026'
import { formatDate, formatPercent } from '../../../i18n/format'
import './back-to-school.css'

// The 5-day Pro trial that stacks with the discount.
const TRIAL_DAYS = 5

interface BackToSchoolInviteProps {
  /** True when the learner also qualifies for the 5-day Pro trial, which stacks with the discount. */
  showTrialTerm?: boolean
}

type Segment = {
  key: string
  label: string
  kind: 'free' | 'half' | 'after'
}

/**
 * The Back-to-School 2026 offer block on /setting#plan.
 *
 * The only genuinely complicated part of this offer is its shape in time — free days, a discounted
 * stretch, then the rate goes back up — so the block renders exactly that and nothing else. The
 * discounted stretch is ONE segment, not one per month: "half price for three months" is a single
 * fact, and repeating it per month reads as filler. The billing cycles inside it are marked as
 * notches on the bar instead, which keeps the detail without the repetition.
 *
 * Showing the return to full rate is deliberate. It is the part a learner is most likely to be
 * surprised by later, and a discount block that hides it is the kind that generates refund emails.
 *
 * The block stays plan-agnostic: the campaign covers Standard and Pro at different prices, so
 * quoting dollars here would mean picking one and misleading anyone reading it about the other.
 * This block owns TIME; the plan cards immediately below own MONEY, already discounted. That is
 * also why there is no button — a single CTA would have to choose a plan on the learner's behalf.
 */
const BackToSchoolInvite: React.FC<BackToSchoolInviteProps> = ({ showTrialTerm }) => {
  const { t } = useTranslation('offers')
  const [timeLeft, setTimeLeft] = useState(() => getBackToSchool2026TimeLeft())

  useEffect(() => {
    // Minute resolution, not seconds: the copy only renders days and hours, so a 1s interval
    // would re-render 3,600 times to change nothing.
    const timer = setInterval(() => setTimeLeft(getBackToSchool2026TimeLeft()), 60_000)
    return () => clearInterval(timer)
  }, [])

  // Formatted in the interface language at each render, so it follows a language switch.
  const closes = formatDate(BACK_TO_SCHOOL_2026.endDate, 'monthDay', { timeZone: 'Asia/Hong_Kong' })

  const remaining =
    timeLeft.days > 0
      ? t('backToSchool.daysLeft', { count: timeLeft.days })
      : timeLeft.hours > 0
        ? t('backToSchool.hoursLeft', { count: timeLeft.hours })
        : t('backToSchool.closingToday')

  const segments: Segment[] = [
    ...(showTrialTerm
      ? [{ key: 'trial', label: t('backToSchool.trialSegment', { days: TRIAL_DAYS }), kind: 'free' as const }]
      : []),
    {
      key: 'discount',
      label: t('backToSchool.discountSegment', { months: BACK_TO_SCHOOL_2026.durationMonths }),
      kind: 'half',
    },
    { key: 'after', label: t('backToSchool.afterSegment'), kind: 'after' },
  ]

  return (
    <section className='bts-invite' aria-labelledby='bts-invite-title'>
      <div className='bts-invite-head'>
        <h3 className='bts-invite-title' id='bts-invite-title'>
          {t('backToSchool.title')}
        </h3>
        <p className='bts-invite-closes'>
          <Trans
            t={t}
            i18nKey='backToSchool.closes'
            values={{ date: closes, remaining }}
            components={{ left: <span className='bts-invite-closes-left' /> }}
          />
        </p>
      </div>

      <p className='bts-invite-lede'>
        {t('backToSchool.lede', { percent: formatPercent(BACK_TO_SCHOOL_2026.discountPercent / 100) })}
      </p>

      {/* An ordered list because this genuinely is a sequence: the order is the information. */}
      <ol className='bts-invite-term'>
        {segments.map((segment) => (
          <li key={segment.key} className={`bts-invite-seg bts-invite-seg--${segment.kind}`}>
            <span className='bts-invite-seg-bar' aria-hidden='true' />
            <span className='bts-invite-seg-label'>{segment.label}</span>
          </li>
        ))}
      </ol>
    </section>
  )
}

export default BackToSchoolInvite
